import pluralize from "pluralize";
import { useCallback, useState } from "react";

import { DriveFileFragment } from "@/features/drive/queries.graphql";
import { downloadAsOBJ, downloadMeshFormat } from "@/utils/download";
import { type MeshFormat } from "@/utils/meshFormats";

import useShowSnackbar from "../useShowSnackbar";

const useDownloadFilesAs = (
  format: MeshFormat,
  label: string,
  download: (file: DriveFileFragment) => Promise<unknown>,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useShowSnackbar();

  const downloadFiles = useCallback(
    async (files: DriveFileFragment[]) => {
      setIsLoading(true);

      try {
        await Promise.all(files.map((file) => download(file)));

        showSuccess(
          `Downloaded ${files.length} ${pluralize("model", files.length)} as ${label}`,
        );
      } catch (error) {
        console.error(`Error downloading model as ${format}:`, error);
        showError(
          `Failed to download ${files.length} ${pluralize("model", files.length)} as ${label}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [download, format, label, showError, showSuccess],
  );

  return { downloadFiles, isLoading };
};

export const useDownloadFilesAsObj = () => {
  const { downloadFiles, isLoading } = useDownloadFilesAs(
    "obj",
    "OBJ",
    downloadAsOBJ,
  );
  return { downloadFilesAsObj: downloadFiles, isLoading };
};

export const useDownloadFilesAsFbx = () => {
  const { downloadFiles, isLoading } = useDownloadFilesAs(
    "fbx",
    "FBX",
    (file) => downloadMeshFormat(file, "fbx"),
  );
  return { downloadFilesAsFbx: downloadFiles, isLoading };
};
